import React, { useState } from 'react';

import { Upload, Loader2, X } from 'lucide-react';

import { showAlert } from '../../utils/sweetAlertUtils';

interface FileUploadProps {
  id: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  title?: string;
  subTitle?: string;
  allowMultiple?: boolean;
  accept?: string[];
  required?: boolean;
  error?: string;
  isUploading?: boolean;
  // When set (e.g. an object URL for a freshly picked file, or a signed URL for an
  // already-uploaded one), shows a preview card above the dropzone.
  previewUrl?: string;
  onRemove?: () => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  id,
  onChange,
  title = 'Upload media or drag and drop',
  allowMultiple = false,
  accept = [],
  required = false,
  error,
  isUploading = false,
  previewUrl,
  onRemove,
}) => {
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      if (accept.length > 0) {
        const invalid = Array.from(e.target.files).filter((file) => {
          const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
          return !accept.map((a) => a.toLowerCase()).includes(ext);
        });
        if (invalid.length > 0) {
          e.target.value = '';
          setUploadedFileName(null);
          showAlert({
            title: 'Invalid file type',
            text: `The following file${invalid.length > 1 ? 's are' : ' is'} not allowed: ${invalid.map((f) => f.name).join(', ')}. Allowed types: ${accept.join(', ')}`,
            icon: 'warning',
          });
          return;
        }
      }
      const fileNames = Array.from(e.target.files)
        .map((file) => file.name)
        .join(', ');
      setUploadedFileName(fileNames);
      onChange(e);
    }
  };

  // Accept string for <input>
  const acceptString = accept.join(',');

  const derivedSubtitle =
    accept.length > 0 ? `Allowed file types: ${accept.join(', ')}` : '';

  return (
    <div className="relative my-6 col-span-full">
      {previewUrl && (
        <div className="mb-3 flex items-center gap-4 rounded border border-gray-200 bg-gray-50 p-3 dark:border-white/20 dark:bg-white/5">
          <img
            src={previewUrl}
            alt="Preview"
            className="h-16 w-16 shrink-0 rounded object-cover shadow-sm"
          />
          <div className="flex-1 text-left">
            <p className="text-sm font-medium text-ink dark:text-white/80">
              Current file
            </p>
            <p className="text-xs text-ink-muted">
              Choose a new file below to replace it
            </p>
          </div>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="shrink-0 rounded-full p-1.5 text-ink-muted transition hover:bg-red-50 hover:text-red-500"
              aria-label="Remove file"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      <input
        id={id}
        name={id}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        multiple={allowMultiple}
        accept={acceptString}
        required={required}
      />
      <label
        htmlFor={id}
        className={`relative flex flex-col items-center gap-2 px-3 py-6 text-sm font-medium text-center
          transition-colors border border-dashed rounded
          ${
            isUploading
              ? 'border-secondary/30 bg-secondary/10 dark:border-secondary/40 dark:bg-secondary/20 cursor-wait'
              : error
                ? 'border-red-300 hover:bg-red-50 cursor-pointer'
                : 'border-gray-300 hover:bg-gray-50 cursor-pointer'
          }`}
      >
        <span
          className={`inline-flex items-center justify-center h-12 px-3 rounded-full ${isUploading ? 'bg-secondary/20 text-secondary dark:bg-secondary/30' : 'bg-gray-100 text-ink-muted'}`}
        >
          {isUploading ? (
            <Loader2 className="w-6 h-6 animate-spin" aria-label="Uploading" />
          ) : (
            <Upload className="w-6 h-6" aria-label="File input icon" />
          )}
        </span>
        <span className="text-ink">
          {isUploading ? 'Uploading...' : title}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
        {derivedSubtitle && !isUploading && (
          <span className="text-xs text-ink-muted">{derivedSubtitle}</span>
        )}
        {!isUploading && (
          <span className="text-xs text-ink-muted">
            Maximum file size: 100MB
          </span>
        )}
        {isUploading && (
          <span className="mt-2 text-xs text-secondary dark:text-secondary/90 font-medium">
            Please wait while the file is being uploaded...
          </span>
        )}
        {!isUploading && uploadedFileName && (
          <span className="mt-2 text-xs text-emerald-500">
            {uploadedFileName} ready to be uploaded
          </span>
        )}
        {!isUploading && error && (
          <span className="mt-2 text-xs text-red-500">{error}</span>
        )}
      </label>
    </div>
  );
};

export default FileUpload;
